/** @jsxImportSource @emotion/react */

// libraries
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { css } from "@emotion/react";

// model, data
import { Column } from "data/table.data";
import { DASHBOARD_COLS } from "./column.data";
import { SAMPLE_BUSINESS_DATA } from "data/sample.data";
import {
  HALF_AND_QUARTERS,
  HalfYear,
  MONTHS,
  Quarter,
  YearMonth,
} from "data/period.data";
import {
  Business,
  MenuType,
  StoreType,
  SUB_TYPE_PARAM_KEYS,
  SubType,
} from "models/business.model";

// components
import Table from "components/common/Table";
import BaseSelect from "components/common/Select/Base";
import BaseInput from "components/common/Input/Base";
import AlertDialog, { AlertData } from "components/common/Dialog/Alert";
import SubtitleCard from "components/common/Card/Subtitle/SubtitleCard";

// utils, api
import { BaseParamData, getStores, updateWorker } from "pages/remotes";
import {
  convertDateToPeriod,
  convertQuarterAndHalfToDate,
  convertYearMonthToDate,
  reverseArray,
  sortList,
} from "utils/converter";

// icons
import { ReactComponent as ChartIcon } from "assets/img/icon/chart.svg";
import { ReactComponent as CompletedIcon } from "assets/img/icon/completed.svg";
import { ReactComponent as UncompletedIcon } from "assets/img/icon/uncompleted.svg";
import { ReactComponent as TotalIcon } from "assets/img/icon/etc.svg";

function Store(data: { storeType: StoreType; subType?: SubType }) {
  const storeType: StoreType = data.storeType;
  const subType: SubType | undefined = data.subType;
  const menuType = (
    subType ? `${storeType}-${subType.toLowerCase()}` : storeType
  ) as MenuType;

  const [searchParams, setSearchParams] = useSearchParams();
  const [spStartDate, spEndDate, spSort, spSample] = [
    searchParams.get("startDate"),
    searchParams.get("endDate"),
    searchParams.get("sort"),
    searchParams.get("sample"),
  ];
  const inputKeyword = useRef<HTMLInputElement>(null);

  const [isInitial, setIsInitial] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isModifyWalkerLoading, setIsModifyWalkerLoading] = useState(false);
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [alertDialogData, setAlertDialogData] = useState<AlertData>({
    message: "",
  });
  const [columnList, setColumnList] = useState<Column[]>([]);
  const [businessList, setBusinessList] = useState<Business[]>([]);

  const [checkedBusiness, setCheckedBusiness] = useState<Set<string>>(
    new Set()
  );

  const [sortedBusinessList, setSortedBusinessList] = useState<Business[]>([]);
  const [isFiltered, setIsFiltered] = useState(false);
  const [filteredBusinessList, setFilteredBusinessList] = useState<Business[]>(
    []
  );
  const [summaryData, setSummaryData] = useState<{
    total: number;
    completed: number;
    uncompleted: number;
    dataTotal: number;
  }>({
    total: 0,
    completed: 0,
    uncompleted: 0,
    dataTotal: 0,
  });

  useEffect(() => {
    setColumnList(
      DASHBOARD_COLS.map((col) => {
        if (col.input)
          return {
            ...col,
            input: {
              submit: (value: string, dataId: string) =>
                modifyWorker(value, dataId),
            },
          };
        else return col;
      })
    );
  }, []);

  useEffect(() => {
    setSortedBusinessList(sortList([...businessList], spSort));
  }, [spSort]);

  useEffect(() => {
    const bizListLen = businessList.length;

    // 기간 없을 경우 호출 후 응답으로 기간 설정, 응답으로 기간 설정할 경우 재호출 방지
    if (spStartDate && spEndDate) {
      if (isInitial) {
        setIsInitial(false);
        if (!bizListLen) loadStoreList(storeType);
      } else {
        loadStoreList(storeType);
      }
    } else if (isInitial) {
      loadStoreList(storeType);
    }
  }, [spStartDate, spEndDate]);

  useEffect(() => {
    const keyword = inputKeyword.current?.value || "";
    if (keyword)
      inputKeywordHandler({
        currentTarget: { value: keyword },
      } as React.FormEvent<HTMLInputElement>); // 검색어가 있는 경우 검색어 필터링
  }, [sortedBusinessList]);

  const onSetSummaryData = (dataList: Business[]) => {
    const total = dataList.length;
    let completed = 0;
    let uncompleted = 0;
    let dataTotal = 0;
    if (total) {
      dataList.forEach((data) => {
        dataTotal += data.totalDataNumber;
        if (data.completedDataNumber === data.totalDataNumber) completed++;
        else uncompleted++;
      });
    }

    setSummaryData({
      total,
      completed,
      uncompleted,
      dataTotal,
    });
  };

  const loadStoreList = (type: StoreType) => {
    if (!type) {
      const tmpAlertData = { ...alertDialogData };
      tmpAlertData.type = "error";
      tmpAlertData.message = "업체 형식 오류로 현황을 불러올 수 없습니다.";
      setAlertDialogData(tmpAlertData);
      setIsAlertOpen(true);
      return;
    }

    const startDate = spStartDate;
    const endDate = spEndDate;
    const subTypeKey = SUB_TYPE_PARAM_KEYS[type] as SubType;

    let paramData: BaseParamData = {};
    if (startDate && endDate) paramData = { startDate, endDate };
    if (subType && subTypeKey)
      paramData = {
        ...paramData,
        [subTypeKey]: subType,
      };

    setIsLoading(true);
    getStores(storeType, paramData)
      .then((res) => {
        const resData = res.data;
        const startDate = resData.startDate;
        const endDate = resData.endDate;
        const businessInfos = spSample
          ? SAMPLE_BUSINESS_DATA
          : resData.businessAccountingInfos;

        if (startDate && endDate) setSearchParams({ startDate, endDate });
        if (startDate && !endDate) {
          // 시작일만 있는 경우: ONLY 은행 입출금 기간 없이 호출할 경우 가장 최신 월 시작일만 반환, 종료일 반환 없음 (0623 의님 확인)
          const endOfMonth = new Date(startDate);
          endOfMonth.setMonth(endOfMonth.getMonth() + 1);
          endOfMonth.setDate(0); // 마지막 날로 설정
          setSearchParams({
            startDate,
            endDate: endOfMonth.toISOString().split("T")[0],
          });
        }

        if (businessInfos) {
          setBusinessList(businessInfos);
          onSetSummaryData(businessInfos);
          setSortedBusinessList(sortList(businessInfos, spSort));
        } else {
          setBusinessList([]);
          onSetSummaryData([]);
          setSortedBusinessList([]);
        }

        setIsLoading(false);
      })
      .catch((err) => {
        setIsLoading(false);
        const tmpAlertData = { ...alertDialogData };
        tmpAlertData.type = "error";
        tmpAlertData.message =
          err.response?.data.message ||
          "업체 현황을 불러오던 중 오류가 발생하였습니다.";
        setAlertDialogData(tmpAlertData);
        setIsAlertOpen(true);
      });
  };

  const onSelectQuarter = (e: React.MouseEvent<HTMLOptionElement>) => {
    const value = e.currentTarget.value || "";
    const { startDate, endDate } = convertQuarterAndHalfToDate(
      value as Quarter | HalfYear
    );
    setSearchParams({ startDate, endDate });
  };
  const onSelectMonth = (e: React.MouseEvent<HTMLOptionElement>) => {
    const value = e.currentTarget.value || "";
    const { startDate, endDate } = convertYearMonthToDate(value as YearMonth);
    setSearchParams({ startDate, endDate });
  };

  const inputKeywordHandler = (e: React.FormEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value || "";
    const loweredVal = value.toLowerCase();
    if (value) {
      setIsFiltered(true);
      let tmpSortedBusinessList = sortedBusinessList.filter((business) => {
        const loweredName = business.name?.toLowerCase() || "";
        return loweredName.includes(loweredVal);
      });
      setFilteredBusinessList(tmpSortedBusinessList);
    } else {
      setIsFiltered(false);
    }
  };

  const modifyWorker = (value: string, dataId: string) => {
    setIsModifyWalkerLoading(true);
    updateWorker(dataId, value)
      .then((res) => {
        setIsModifyWalkerLoading(false);
        const tmpAlertData = { ...alertDialogData };
        tmpAlertData.type = "success";
        tmpAlertData.message = `담당자가 정상적으로 수정되었습니다.\n변경된 담당자를 반영하여 업체 현황을 갱신합니다.`;
        setAlertDialogData(tmpAlertData);
        setIsAlertOpen(true);
        loadStoreList(storeType);
      })
      .catch((err) => {
        console.error(err);
        setIsModifyWalkerLoading(false);
        const tmpAlertData = { ...alertDialogData };
        tmpAlertData.type = "error";
        tmpAlertData.message =
          err.response?.data.message || "담당자 수정 중 오류가 발생하였습니다.";
        setAlertDialogData(tmpAlertData);
        setIsAlertOpen(true);
      });
  };

  return (
    <div>
      <section
        css={css`
          padding: 48px 0;
          display: flex;
          align-items: center;
        `}
      >
        <h1
          css={css`
            font-size: 28px;
            font-weight: 500;
          `}
        >
          전체 업체 현황
        </h1>

        <SubtitleCard>
          <ChartIcon />
          <span>전체 ({summaryData.total})</span>
          <CompletedIcon />
          <span>완료 ({summaryData.completed})</span>
          <UncompletedIcon />
          <span>미완료 ({summaryData.uncompleted})</span>
          <TotalIcon />
          <span>데이터 총계 ({summaryData.dataTotal})</span>
        </SubtitleCard>
      </section>

      <section
        css={css`
          display: flex;
          justify-content: space-between;
        `}
      >
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: 12px;
          `}
        >
          <BaseSelect
            name="반기/분기"
            options={reverseArray(HALF_AND_QUARTERS)}
            selectedOption={convertDateToPeriod(spStartDate, spEndDate)}
            onSelect={onSelectQuarter}
          />
          <BaseSelect
            name="월별검색"
            options={reverseArray(MONTHS)}
            selectedOption={convertDateToPeriod(spStartDate, spEndDate)}
            onSelect={onSelectMonth}
          />
          <BaseInput
            placeholder="업체명 검색"
            ref={inputKeyword}
            onInput={inputKeywordHandler}
            css={css`
              box-sizing: border-box;
            `}
          />
        </div>

        <div
          css={css`
            display: flex;
            justify-content: flex-end;
            gap: 12px;
          `}
        >
          {/* <BaseButton
            variant="outlined"
            color={PALETTE.primary}
            disabled={checkedBusiness.size === 0}
            onClick={() => console.log(checkedBusiness)}
          >
            선택 다운로드
          </BaseButton> */}
        </div>
        {/* <ReportButton
          storeType={storeType}
          reportType="submit"
          bizId={
            checkedBusiness.size === 1 && checkedBusiness.values().next().value
          }
          successCallback={() => loadStoreList(storeType)}
        /> */}
        <AlertDialog
          isOpen={isAlertOpen}
          setIsOpen={setIsAlertOpen}
          alertData={alertDialogData}
        />
      </section>

      <section
        css={css`
          margin-top: 12px;
        `}
      >
        <Table
          isLoading={isLoading}
          minWidth={1200}
          columns={columnList}
          dataList={isFiltered ? filteredBusinessList : sortedBusinessList}
          menuType={menuType}
          checkedSet={checkedBusiness}
          setCheckedSet={setCheckedBusiness}
          loadData={() => loadStoreList(storeType)}
        />
      </section>
    </div>
  );
}

export default Store;
